import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { ChatStateService } from './chat-state.service';
import { GraphModalService } from './graph-modal.service';
import { ChunkToEdit, SuggestionContext, ChunkUpdate } from '../../pages/chat/components/edit-chunks-modal/edit-chunks-modal.component';

@Injectable()
export class ChunkEditorService {
  isEditChunksModalOpen = false;
  chunksToEdit: ChunkToEdit[] = [];
  aiSuggestedChunks = false;
  isSavingChunks = false;
  isAnalyzingChunks = false;
  suggestionContext: SuggestionContext | null = null;

  constructor(
    private apiService: ApiService,
    private chatState: ChatStateService,
    private graphModal: GraphModalService
  ) {}

  handleEditChunksDirectly(event: { nodeId: string, originalValue: string, editedValue: string }): void {
    this.chunksToEdit = [{
      chunkId: event.nodeId,
      originalValue: event.originalValue,
      suggestedValue: event.editedValue
    }];
    this.isEditChunksModalOpen = true;
    this.graphModal.selectedNode = null; // Close graph sidebar natively
    this.graphModal.selectedEdge = null;
  }

  handleSuggestChunkEdits(event: { nodeId: string, oldValue: string, label: string, desiredChange: string }): void {
    this.chatState.clearNotifications();
    this.suggestionContext = {
      nodeId: event.nodeId,
      oldValue: event.oldValue,
      nodeLabel: event.label,
      desiredChange: event.desiredChange
    };

    this.chunksToEdit = [];
    this.aiSuggestedChunks = false;
    this.isEditChunksModalOpen = true;
    this.isAnalyzingChunks = true;
    this.graphModal.selectedNode = null;
    this.graphModal.selectedEdge = null;

    this.apiService.proposeChunkUpdates(event.nodeId, event.oldValue, event.label, event.desiredChange)
      .subscribe({
        next: (response: any) => {
          if (response.chunks && response.chunks.length > 0) {
            this.chunksToEdit = response.chunks.map((c: any) => ({
              chunkId: c.chunkId,
              originalValue: c.originalValue || c.oldValue,
              suggestedValue: c.suggestedValue || c.newValue
            }));
            this.aiSuggestedChunks = response.aiSuggested || false;
          } else {
            this.chatState.setError('No related editable chunks found for this item.');
            this.chunksToEdit = [];
          }
          this.isAnalyzingChunks = false;
        },
        error: (err: any) => {
          this.handleError(err, `Failed to get chunk suggestions for node ${event.nodeId}`);
          this.chunksToEdit = [];
          this.isAnalyzingChunks = false;
        }
      });
  }

  handleSaveChunkUpdates(updatedChunks: ChunkUpdate[]): void {
    if (!updatedChunks || updatedChunks.length === 0) {
      this.closeChunkEditor();
      return;
    }

    this.isSavingChunks = true;
    this.chatState.clearNotifications();

    this.apiService.updateChunksAndReingest(updatedChunks, '')
      .subscribe({
        next: (response: any) => {
          const msg = (response.message || "Chunks updated and re-ingestion started.") +
            " we recommend testing the changes in a new chat session.";
          this.chatState.setSuccessMessage(msg);
          this.closeChunkEditor();
          this.graphModal.closeModal(); // Close graph modal as structure changed
        },
        error: (err: any) => {
          this.handleError(err, 'Failed to save chunk updates and re-ingest');
          this.isSavingChunks = false;
        }
      });
  }

  closeChunkEditor(): void {
    this.isEditChunksModalOpen = false;
    this.chunksToEdit = [];
    this.aiSuggestedChunks = false;
    this.suggestionContext = null;
    this.isSavingChunks = false;
    this.chatState.setError(null);
  }

  private handleError(err: any, contextMessage: string): void {
    console.error(contextMessage, err);
    const errorMessage = err?.message || err?.error || 'Unknown error';
    this.chatState.setError(`${contextMessage}: ${errorMessage}`);
  }
}
